import React, { useEffect, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import Typography from 'britive-design-system/core/components/typography'
import Textarea from 'britive-design-system/core/components/textarea'
import ModalPopup from 'britive-design-system/core/components/modal-popup'
import Spinner from 'britive-design-system/core/components/spinner'
import Snackbar from 'britive-design-system/core/components/snackbar'
import Tooltip from 'britive-design-system/core/components/tooltip'
import toast from 'utils/toast'
import { errorCode } from './constants'
import ITSMCheckoutSettings from './ITSMCheckoutSettings'
import { getFormErrors, isJustificationRegexRequired } from './utils'
import infoIcon from 'static/images/infoIcon.svg'
import './index.scss'

const CSS_CLASS_PREFIX = 'common-approval-modal'

const ApprovalModal = ({
  approversDetails = {},
  entityName,
  onSubmit,
  onModalClose,
  action,
  approvalInfoText,
  entityType,
  advancedSettings = {},
  onTicketSearch,
  ticketSearchResults = [],
  findTicketDetailsById,
}) => {
  const [justification, setJustification] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [checkoutItsmData, setCheckoutItsmData] = useState({})
  const [errors, setErrors] = useState([])
  const { userIds: userNames, tags: groupNames, managerApproval, manager } =
    approversDetails || {}

  const requiresApproval = !isEmpty(approversDetails)
  const hasExistingApprovalData = !isEmpty(advancedSettings?.approvalRequestData)

  useEffect(() => {
    if (advancedSettings?.approvalRequestData) {
      setJustification(advancedSettings.approvalRequestData.justification)
      setCheckoutItsmData({
        ticketId: advancedSettings.approvalRequestData.ticketId,
        ticketType: advancedSettings.approvalRequestData.ticketType,
      })
    }
  }, [JSON.stringify(advancedSettings?.approvalRequestData)])

  const handleSubmit = async () => {
    try {
      const formErrors = getFormErrors(
        advancedSettings,
        { justification, ...checkoutItsmData },
        requiresApproval
      )

      setErrors(formErrors)
      if (!isEmpty(formErrors)) {
        return
      }

      setIsSubmitting(true)
      await onSubmit({
        ...checkoutItsmData,
        justification,
      })
      setIsSubmitting(false)
      onModalClose(true)
    } catch ({ response }) {
      let reason = response?.data?.message || ''
      const { data } = response
      if (data?.errorCode === errorCode) {
        toast({
          title: 'Access request is sent for approval.',
          type: 'success',
          time: 'normal',
        })
      } else {
        setErrors([reason])
      }

      setIsSubmitting(false)
    }
  }

  const requiredApprovalModalActionButtons = [
    {
      text: requiresApproval ? 'Send Request' : 'Proceed to Checkout',
      variant: 'primary',
      onClick: handleSubmit,
      size: 'large',
    },
    {
      text: 'Cancel',
      variant: 'secondary',
      onClick: onModalClose,
      size: 'large',
    },
  ]

  const handleInputChange = input => {
    // Removed HTML tags using regex
    const sanitizedInput = input.replace(/<\/?[^>]+(>|$)/g, '')
    setJustification(sanitizedInput)
  }
  const renderLabel = () => {
    const condition = managerApproval?.condition

    if (condition === 'All' || condition === 'Any') {
      const approverType = condition === 'All' ? 'AND' : 'OR'
      return `This request must be approved by Manager ${approverType} Approvers`
    }
    if (condition === 'Manager') {
      return 'This request must be  approved by'
    }

    return 'This request must be approved by:'
  }

  return (
    <>
      {isSubmitting && <Spinner overlay size="medium" message="" />}
      <ModalPopup
        width={512}
        title={`${entityName} (${action})`}
        buttons={requiredApprovalModalActionButtons}
        onCancel={onModalClose}
      >
        <div>
          {errors.length ? (
            <>
              <Snackbar
                errorList={errors}
                allowClosing
                onClose={() => setErrors([])}
              />
              <br />
            </>
          ) : null}
          <div className={`${CSS_CLASS_PREFIX}-spacing`}>
            {requiresApproval && (
              <>
                <div className={`${CSS_CLASS_PREFIX}-spacing`}>
                  <Typography variant="label1">
                    Approval is required to access to this {entityType}.
                  </Typography>
                </div>
                <div className={`${CSS_CLASS_PREFIX}-label`}>
                  <Typography variant="label1">{renderLabel()}</Typography>
                </div>
                <div className={`${CSS_CLASS_PREFIX}-spacing`}>
                  {!isEmpty(managerApproval) && (
                    <>
                      <div className={`${CSS_CLASS_PREFIX}-label`}>
                        <Typography variant="label2">{'Manager'}</Typography>
                      </div>
                      <div className={`${CSS_CLASS_PREFIX}-label`}>
                        <Typography variant="label1">- {manager}</Typography>
                      </div>
                    </>
                  )}
                  {(userNames?.length || groupNames?.length) > 0 && (
                    <div className={`${CSS_CLASS_PREFIX}-label`}>
                      <Typography variant="label2">{'Approvers'}</Typography>
                    </div>
                  )}
                  {userNames &&
                    userNames?.map(user => {
                      return (
                        <div key={user} className={`${CSS_CLASS_PREFIX}-label`}>
                          <Typography variant="label1"> - {user} </Typography>
                        </div>
                      )
                    })}
                  {groupNames &&
                    groupNames?.map(group => {
                      return (
                        <div key={group} className={`${CSS_CLASS_PREFIX}-label`}>
                          <Typography variant="label1"> - {group} </Typography>
                        </div>
                      )
                    })}
                </div>
              </>
            )}

            {!hasExistingApprovalData ? (
              <div className={`${CSS_CLASS_PREFIX}-spacing`}>
                <Textarea
                  width="450px"
                  label={
                    isJustificationRegexRequired(
                      advancedSettings,
                      requiresApproval
                    ) ? (
                      <div className={`${CSS_CLASS_PREFIX}-justification-label`}>
                        <Typography variant="label2">Justification</Typography>
                        <Tooltip
                          title={`RegEx: ${advancedSettings?.justificationSetting?.justificationRegex}`}
                          position="top"
                        >
                          <img src={infoIcon} />
                        </Tooltip>
                      </div>
                    ) : (
                      'Justification'
                    )
                  }
                  value={justification}
                  onChange={e => handleInputChange(e.target.value)}
                />
              </div>
            ) : null}

            {!isEmpty(advancedSettings?.itsmSetting) &&
              advancedSettings.itsmSetting?.isITSMEnabled && (
                <ITSMCheckoutSettings
                  itsmData={advancedSettings.itsmSetting}
                  checkoutItsmData={checkoutItsmData}
                  onChange={setCheckoutItsmData}
                  onTicketSearch={onTicketSearch}
                  ticketSearchResults={ticketSearchResults}
                  findTicketDetailsById={findTicketDetailsById}
                  setErrors={setErrors}
                />
              )}
            <br />
            {requiresApproval && (
              <>
                <div className={`${CSS_CLASS_PREFIX}-label`}>
                  <Typography variant="label1">
                    {approvalInfoText ||
                      `You will be notified about the status of this request.`}
                  </Typography>
                </div>
                <div className={`${CSS_CLASS_PREFIX}-label`}>
                  <Typography variant="label1">
                    You can also track this request under the “My Requests” section.
                  </Typography>
                </div>
              </>
            )}
          </div>
        </div>
      </ModalPopup>
    </>
  )
}

export default ApprovalModal
